import { Box, Breadcrumbs, Link, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridDataSource, GridDataSourceCache, GridDataSourceCacheDefault, GridGetRowsParams, GridGetRowsResponse, GridRenderCellParams, GridRowModesModel, useGridApiRef } from "@mui/x-data-grid";
import React from "react";
import CustomDataGrid from "../../Common/CustomDataGrid";
import { formatNumber } from "../../Common/FormatNumber";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import CustomNoRowsOverlay from "../../Common/CustomNoRowsOverlay";

export default function ItemsPurchased() {
    const customer_id = localStorage.getItem('customer_id');
    const [itemsPurchased, setItemsPurchased] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const apiRef = useGridApiRef();
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [lowTTLCache, setLowTTLCache] = React.useState<GridDataSourceCache>(() => new GridDataSourceCacheDefault({ ttl: 1000 * 10 }));
    const [navigate, setNavigate] = React.useState<boolean>(false);
    const customer_name = localStorage.getItem('customer_name') as string;
    const [value, setValue] = React.useState(0);
    const [itemsSoldToCustomerType, setItemsSoldToCustomerType] = React.useState([]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        if (newValue === 1) {
            setLoading(true);
            fetchItemsSoldToCustomerType();
        }
    };

    const fetchItemsSoldToCustomerType = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/customers/${customer_id}/get-items-sold-to-customer-type/?entity_id=${localStorage.getItem('entity_id')}`, {
            method: 'GET',
        });
            if (!response.ok) {
                throw new Error('Failed to fetch items sold to customer type');
            }
            const data = await response.json();
            setItemsSoldToCustomerType(data.rows);
        } catch (error) {
            console.error('Error fetching items sold to customer type:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Product Purchased', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'product_grp_1', headerName: 'Product Group 1', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'AnnualInvoices', headerName: 'Number of Orders', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'AnnualQty', headerName: 'Items Purchased', width: 200, headerAlign: 'center', align: 'center', 
            renderCell: (params: GridRenderCellParams) => {
                return <span>{new Intl.NumberFormat().format(params.value)}</span>;
            },
        },
        { 
            field: 'AnnualSales', headerName: 'Sales ($)', width: 150, headerAlign: 'center', align: 'center', type: 'number',
            renderCell: (params: GridRenderCellParams) => {
                return <span>{formatNumber(params.value)}</span>;
            },
        },
        { 
            field: 'AnnualGPPct', headerName: 'Annual Gross Profit (%)', width: 150, headerAlign: 'center', align: 'center', type: 'number',
            renderCell: (params: GridRenderCellParams) => {
                return <span>{params.value.toFixed(1)}%</span>;
            },
        },
        { field: 'VelocityCode', headerName: 'Order Frequency', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'VolumeCode', headerName: 'Purchase Volume', width: 150, headerAlign: 'center', align: 'center' },
    ];


    const dataSource: GridDataSource = {
        getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
  
            const urlParams = new URLSearchParams();
            // if (params.paginationModel.page > 0) {
            urlParams.set('paginationModel', JSON.stringify(params.paginationModel));
            // }
            if (params.sortModel.length > 0) {
                urlParams.set('sortModel', JSON.stringify(params.sortModel));
            }
            if (params.filterModel.items.length > 0) {
                urlParams.set('filterModel', JSON.stringify(params.filterModel));
            }
  
            const urlParamsString = urlParams.size > 0 ? `?${urlParams.toString()}&entity_id=${localStorage.getItem('entity_id')}` : `?entity_id=${localStorage.getItem('entity_id')}`;
            const response = await fetch(`/customers/${customer_id}/get-items-purchased/${urlParamsString}`, {
              method: 'GET',
            });
            const data = await response.json();
            return {
                rows: data.rows,
                rowCount: data.rowCount,
            };
        },
    }

    const marketColumns: GridColDef[] = [
        { field: 'id', headerName: 'SKU Code', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'name', headerName: 'Product Group 1', width: 150, headerAlign: 'center', align: 'center' },
        { field: 'buying_accounts', headerName: 'Buying Accounts', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: () => {
                return <span style={{ textAlign: 'center' }}>Buying <br/> Accounts</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span>{new Intl.NumberFormat().format(params.value)}</span>;
            },
         },
        { field: 'pg_annual_invoices', headerName: 'Invoices #', width: 150, type: 'number', headerAlign: 'center', align: 'center', 
            renderCell: (params: GridRenderCellParams) => {
                return <span>{new Intl.NumberFormat().format(params.value)}</span>;
            },
        },
        { field: 'pg_annual_sales', headerName: 'Sales ($)', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
         },
        { field: 'pg_gp_pct', headerName: 'Gross Profit (%)', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{params.value.toFixed(1)}%</span>;
            },
        },
        { field: 'total_avg_gp_pct', headerName: 'Average Gross Profit (%)', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: () => {
                return <span style={{ textAlign: 'center' }}>Average <br/>Gross Profit (%)</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{params.value.toFixed(2)}%</span>;
            },
        },
        { field: 'avg_trans_size', headerName: 'Avg. Transaction Size', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: () => {
                return <span style={{ textAlign: 'center' }}>Avg. <br/>Transaction Size</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        { field: 'average_unit_size', headerName: 'Average Quantity', width: 150, type: 'number', headerAlign: 'center', align: 'center',
            renderHeader: () => {
                return <span style={{ textAlign: 'center' }}>Average <br/>Quantity</span>;
            },
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{new Intl.NumberFormat('en-US').format(params.value)}</span>;
            },
        },
        {
            field: 'vel_a', headerName: 'Vel. A', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vel_b', headerName: 'Vel. B', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vel_c', headerName: 'Vel. C', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vel_d', headerName: 'Vel. D', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_a', headerName: 'Vol. A', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_b', headerName: 'Vol. B', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_c', headerName: 'Vol. C', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
        {
            field: 'vol_d', headerName: 'Vol. D', width: 120, type: 'number', headerAlign: 'center', align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return <span style={{ color: params.value > 0 ? '#000' : 'red' }}>{formatNumber(params.value)}</span>;
            },
        },
    ]



    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <Typography variant="h4" sx={{ fontSize: 'clamp(1rem, 1.5rem, 2rem) !important', fontFamily: 'Trebuchet MS', textTransform: 'capitalize', fontWeight: 'bold' }}>Items Purchased</Typography>
            <Typography variant="body1" sx={{ fontSize: 'clamp(0.9rem, 0.9rem, 0.9rem) !important', fontFamily: 'Trebuchet MS', textTransform: 'capitalize' }}>Here you can see the items purchased by the customer.</Typography>
            <Box sx={{ height: '100%', width: '100%', marginTop: 2 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0, boxShadow: 'none', marginTop: 2, alignItems: 'flex-start' }}>
                    <Link underline="hover" color="inherit" href={`/customers/${customer_id}?entity_id=${localStorage.getItem('entity_id')}`}>{`Back to ${customer_name} panel`}</Link>
                    <Typography sx={{ color: 'primary.main', fontSize: 'clamp(0.9rem, 0.9rem, 0.9rem) !important' }}>Items Purchased</Typography>
                </Breadcrumbs>
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                marginTop: 2,
                borderRadius: '6px !important',
                backgroundColor: 'white',
                border: '1px solid #f0f0f0 !important',
            }}>
                <Box sx={{ width: '100%', typography: 'body1', height: '100%', margin: 0, padding: 0, borderRadius: '6px !important', backgroundColor: 'white' }}>
                <TabContext value={value}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%', padding: 0 }}>
                        <TabList onChange={handleChange} aria-label="items purchased tabs" style={{ justifyContent: 'flex-start' }}>
                            <Tab label="List of Items Purchased" wrapped style={{ textTransform: 'capitalize' }} value={0} />
                            <Tab label="Items sold to this Customer Type" wrapped style={{ textTransform: 'capitalize' }} value={1} />
                        </TabList>
                    </Box>
                    <TabPanel value={0} sx={{ margin: 0, padding: 0, width: '100%', height: '100%' }}>
                        <CustomDataGrid
                            title=""
                            url={`/customers/${customer_id}/get-items-purchased`}
                            exportUrl={`/customers/${customer_id}/get-items-purchased/export`}
                            columns={columns}
                            sortModel={[]}
                            gridDataSource={dataSource}
                            pageSize={30}
                        />
                    </TabPanel>
                    <TabPanel value={1} sx={{ margin: 0, padding: 0, width: '100%', height: '100%' }}>
                        <DataGrid
                            loading={loading}
                            showToolbar
                            columns={marketColumns}
                            rows={itemsSoldToCustomerType}
                            pagination
                            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 }, rowCount: 0 } }}
                            pageSizeOptions={[5, 10, 20, 50, 100]}
                            slots={{
                                noRowsOverlay: () => <CustomNoRowsOverlay message="No data available" />,
                            }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                            sx={{
                                width: '100%',
                                height: 'auto',
                                fontFamily: 'inherit',
                                maxHeight: 'auto',
                                borderRadius: '6px',
                                overflowY: 'auto !important',
                                overflowX: 'auto !important',
                                border: 'none',
                                '& .MuiDataGrid-row': {
                                    minHeight: '30px !important',
                                },
                                '& .MuiDataGrid-cell': {
                                    whiteSpace: 'normal !important',
                                    lineHeight: 'inherit !important',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                fontSize: '0.75rem',
                            }}
                        />
                    </TabPanel>
                </TabContext>
                </Box>
            </Box>
        </Box>
    )
}